import { useState, useEffect, useCallback, useMemo } from "react";
import Header from "src/header";
import AdminHeader from "../AdminHeader";
import { useCurrentOrganization } from "src/contexts/OrganizationContext";
import SelectInput from "src/components/inputs/SelectInput";
import SubmitButton from "src/components/SubmitButton";
import { EhrProvider, OAuthProvider } from "src/shared/types";
import { apiGet, apiPatch, apiPost, apiDelete } from "src/utils/api";
import { useToast } from "src/components/Toast";
import { useConnectionStatusPortal } from "src/hooks/useConnectionStatusPortal";
import { useAuth } from "src/contexts/AuthContext";
import { twMerge } from "tailwind-merge";
import { PlusIcon } from "@heroicons/react/24/outline";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "src/components/table/StaticDataTable";
import { SortDirection } from "src/components/table/useTableData";
import ActionsMenu from "src/components/ActionsMenu";
import EpicConfigTab from "./EpicConfigTab";
import CernerConfigTab from "./CernerConfigTab";
import OAuthEpicTab from "./OAuthEpicTab";
import OAuthGoogleTab from "./OAuthGoogleTab";
import OAuthMicrosoftTab from "./OAuthMicrosoftTab";
import OAuthWebexTab from "./OAuthWebexTab";
import OAuthZoomTab from "./OAuthZoomTab";
import GoogleMapsConfigTab from "./GoogleMapsConfigTab";
import PauboxConfigTab from "./PauboxConfigTab";
import Hl7ConfigTab from "./Hl7ConfigTab";
import DocumentTypesSection from "./DocumentTypesSection";
import ClinicalNoteTypesSection from "./ClinicalNoteTypesSection";

const EHR_TABS: { key: EhrProvider; label: string }[] = [
  { key: "epic", label: "Epic" },
  { key: "cerner", label: "Cerner" },
];

const OAUTH_TABS: { key: OAuthProvider; label: string }[] = [
  { key: "epic", label: "Epic" },
  { key: "google", label: "Google" },
  { key: "microsoft", label: "Microsoft" },
  { key: "webex", label: "Webex" },
  { key: "zoom", label: "Zoom" },
];

const ALLOWED_DOMAIN_COLUMNS: StaticDataColumnProps[] = [
  { title: 'Domain', sortable: true, defaultSort: true, defaultSortDirection: SortDirection.ASC },
];

const domainToRow = (domain: string, onDelete: (domain: string) => void): StaticDataRow[] => {
  return [
    {
      name: 'Domain',
      value: domain,
      element: <span className="text-sm font-mono text-gray-900">{domain}</span>,
    },
    {
      name: 'Actions',
      value: '',
      element: (
        <ActionsMenu
          actions={[
            { id: 'delete', label: 'Delete', onClick: () => onDelete(domain) },
          ]}
        />
      ),
    },
  ];
};

const GeneralSettings = () => {
  const organization = useCurrentOrganization();
  const { setStorage } = useAuth();
  const [organizationName, setOrganizationName] = useState<string>(organization.name);
  const [ehrProvider, setEhrProvider] = useState<EhrProvider>(organization.ehr_provider);
  const [ehrTab, setEhrTab] = useState<EhrProvider>(organization.ehr_provider);
  const [oauthTab, setOauthTab] = useState<OAuthProvider>("epic");
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [directoryJobsEnabled, setDirectoryJobsEnabled] = useState(true);

  const [clientId, setClientId] = useState("");
  const [epicFrontendClientId, setEpicFrontendClientId] = useState("");
  const [baseOauthUrl, setBaseOauthUrl] = useState("");
  const [epicClientSecret, setEpicClientSecret] = useState("");

  const [cernerClientId, setCernerClientId] = useState("");
  const [cernerClientSecret, setCernerClientSecret] = useState("");
  const [cernerSystemId, setCernerSystemId] = useState("");
  const [cernerOrganizationId, setCernerOrganizationId] = useState("");

  const [oauthEpicAdminLogin, setOauthEpicAdminLogin] = useState(false);
  const [oauthEpicDashboardLogin, setOauthEpicDashboardLogin] = useState(false);
  const [oauthGoogleAdminLogin, setOauthGoogleAdminLogin] = useState(false);
  const [oauthGoogleDashboardLogin, setOauthGoogleDashboardLogin] = useState(false);
  const [oauthGoogleClientId, setOauthGoogleClientId] = useState("");
  const [oauthGoogleClientSecret, setOauthGoogleClientSecret] = useState("");
  const [oauthMicrosoftAdminLogin, setOauthMicrosoftAdminLogin] = useState(false);
  const [oauthMicrosoftDashboardLogin, setOauthMicrosoftDashboardLogin] = useState(false);
  const [oauthMicrosoftClientId, setOauthMicrosoftClientId] = useState("");
  const [oauthMicrosoftClientSecret, setOauthMicrosoftClientSecret] = useState("");
  const [oauthWebexAdminLogin, setOauthWebexAdminLogin] = useState(false);
  const [oauthWebexDashboardLogin, setOauthWebexDashboardLogin] = useState(false);
  const [oauthWebexClientId, setOauthWebexClientId] = useState("");
  const [oauthWebexClientSecret, setOauthWebexClientSecret] = useState("");
  const [oauthWebexScope, setOauthWebexScope] = useState("");
  const [oauthZoomAdminLogin, setOauthZoomAdminLogin] = useState(false);
  const [oauthZoomDashboardLogin, setOauthZoomDashboardLogin] = useState(false);
  const [oauthZoomClientId, setOauthZoomClientId] = useState("");
  const [oauthZoomClientSecret, setOauthZoomClientSecret] = useState("");

  // Google Maps, Paubox
  const [googleMapsKey, setGoogleMapsKey] = useState("");
  const [pauboxApiKey, setPauboxApiKey] = useState("");
  const [pauboxBaseUrl, setPauboxBaseUrl] = useState("");
  const [pauboxEmailAddress, setPauboxEmailAddress] = useState("");
  const [hl7ConnectorJwtSecret, setHl7ConnectorJwtSecret] = useState("");

  const [newDomain, setNewDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const disableInputs = isLoading || isTestingConnection;
  const { showToast, Toast } = useToast();
  const { connectionStatus, updateConnectionStatus, isLoading: isLoadingConnectionStatus } = useConnectionStatusPortal();
  const [jwksCopied, setJwksCopied] = useState(false);
  const jwksUrl = typeof window !== "undefined" ? `${window.location.origin}/api/${organization.id}/epic/jwks.json` : "";

  const ehrProviderOptions = [
    { value: "epic", label: "EPIC" },
    { value: "cerner", label: "CERNER" },
  ];

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiGet(`/api/organizations/${organization.id}/settings`);
      const data = await response.json();
      if (data.error) return;

      setOrganizationName(data.name ?? organization.name);
      setDirectoryJobsEnabled(data.directory_jobs_enabled ?? true);
      setBaseOauthUrl(data.base_oauth_url ?? "");
      setClientId(data.client_id ?? "");
      setEpicClientSecret(data.epic_client_secret ?? "");
      setEpicFrontendClientId(data.epic_frontend_client_id ?? "");
      setCernerClientId(data.cerner_client_id ?? "");
      setCernerClientSecret(data.cerner_client_secret ?? "");
      setCernerSystemId(data.cerner_system_id ?? "");
      setCernerOrganizationId(data.cerner_organization_id ?? "");
      setOauthWebexClientId(data.oauth_webex_client_id ?? "");
      setOauthWebexClientSecret(data.oauth_webex_client_secret ?? "");
      setOauthWebexScope(data.oauth_webex_scope ?? "");
      setOauthZoomClientId(data.oauth_zoom_client_id ?? "");
      setOauthZoomClientSecret(data.oauth_zoom_client_secret ?? "");

      setOauthEpicAdminLogin(data.oauth_epic_admin_login);
      setOauthEpicDashboardLogin(data.oauth_epic_dashboard_login);
      setOauthGoogleAdminLogin(data.oauth_google_admin_login);
      setOauthGoogleDashboardLogin(data.oauth_google_dashboard_login);
      setOauthGoogleClientId(data.oauth_google_client_id ?? "");
      setOauthGoogleClientSecret(data.oauth_google_client_secret ?? "");
      setOauthMicrosoftAdminLogin(data.oauth_microsoft_admin_login);
      setOauthMicrosoftDashboardLogin(data.oauth_microsoft_dashboard_login);
      setOauthMicrosoftClientId(data.oauth_microsoft_client_id ?? "");
      setOauthMicrosoftClientSecret(data.oauth_microsoft_client_secret ?? "");
      setOauthWebexAdminLogin(data.oauth_webex_admin_login);
      setOauthWebexDashboardLogin(data.oauth_webex_dashboard_login);
      setOauthZoomAdminLogin(data.oauth_zoom_admin_login);
      setOauthZoomDashboardLogin(data.oauth_zoom_dashboard_login);

      setGoogleMapsKey(data.google_maps_key ?? "");
      setPauboxApiKey(data.paubox_api_key ?? "");
      setPauboxBaseUrl(data.paubox_base_url ?? "");
      setPauboxEmailAddress(data.paubox_email_address ?? "");
      setHl7ConnectorJwtSecret(data.hl7_connector_jwt_secret ?? "");
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [organization.id, organization.name]);

  const fetchAllowedDomains = useCallback(async () => {
    try {
      const response = await apiGet(`/api/organizations/${organization.id}/allowed-domains`);
      const data = await response.json();
      setAllowedDomains(data.allowed_domains || []);
    } catch (error) {
      console.error("Error fetching allowed domains:", error);
    }
  }, [organization.id]);

  const addDomain = useCallback(async (domain: string) => {
    try {
      const response = await apiPost(`/api/organizations/${organization.id}/allowed-domains`, { domain });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to add domain");
      }
      setAllowedDomains(data.allowed_domains);
    } catch (error) {
      console.error("Error adding domain:", error);
      showToast("Failed to add domain.", "error");
    }
  }, [organization.id, showToast]);

  const removeDomain = useCallback(async (domain: string) => {
    try {
      const response = await apiDelete(`/api/organizations/${organization.id}/allowed-domains`, { domain });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to remove domain");
      }
      setAllowedDomains(data.allowed_domains);
      if (data.removed_count > 0) {
        showToast(`Removed ${data.removed_count} user(s) with domain ${domain}.`, "success");
      }
    } catch (error) {
      console.error("Error removing domain:", error);
      showToast("Failed to remove domain.", "error");
    }
  }, [organization.id, showToast]);

  const isDomainValid = useCallback((domain: string) => {
    const trimmed = domain.trim();
    return trimmed.length > 0 && !trimmed.includes('@') && trimmed.includes('.');
  }, []);

  const handleAddDomain = useCallback(() => {
    const trimmed = newDomain.trim();
    if (!trimmed) return;
    if (trimmed.includes('@')) {
      showToast("Domain must not contain an @ symbol.", "error");
      return;
    }
    if (!trimmed.includes('.')) {
      showToast("Domain must contain a period.", "error");
      return;
    }
    if (allowedDomains.includes(trimmed)) {
      showToast("Domain already exists.", "error");
      return;
    }
    addDomain(trimmed);
    setNewDomain("");
  }, [newDomain, allowedDomains, addDomain, showToast]);

  const handleDeleteDomain = useCallback((domain: string) => {
    if (!window.confirm(`Warning, if you continue, you will remove all users from your organization that have a domain of ${domain}`)) return;
    removeDomain(domain);
  }, [removeDomain]);

  const domainRows = useMemo(() => {
    return allowedDomains.map((domain) => domainToRow(domain, handleDeleteDomain));
  }, [allowedDomains, handleDeleteDomain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const settingsData: Record<string, string | boolean | string[]> = {
        name: organizationName.trim(),
        directory_jobs_enabled: directoryJobsEnabled,
        new_ehr_provider: ehrProvider,
        base_oauth_url: baseOauthUrl.trim(),
        client_id: clientId.trim(),
        epic_client_secret: epicClientSecret.trim(),
        epic_frontend_client_id: epicFrontendClientId.trim(),
        cerner_client_id: cernerClientId.trim(),
        cerner_client_secret: cernerClientSecret.trim(),
        cerner_system_id: cernerSystemId.trim(),
        cerner_organization_id: cernerOrganizationId.trim(),
        oauth_epic_admin_login: oauthEpicAdminLogin,
        oauth_epic_dashboard_login: oauthEpicDashboardLogin,
        oauth_google_admin_login: oauthGoogleAdminLogin,
        oauth_google_dashboard_login: oauthGoogleDashboardLogin,
        oauth_google_client_id: oauthGoogleClientId.trim(),
        oauth_google_client_secret: oauthGoogleClientSecret.trim(),
        oauth_microsoft_admin_login: oauthMicrosoftAdminLogin,
        oauth_microsoft_dashboard_login: oauthMicrosoftDashboardLogin,
        oauth_microsoft_client_id: oauthMicrosoftClientId.trim(),
        oauth_microsoft_client_secret: oauthMicrosoftClientSecret.trim(),
        oauth_webex_admin_login: oauthWebexAdminLogin,
        oauth_webex_dashboard_login: oauthWebexDashboardLogin,
        oauth_zoom_admin_login: oauthZoomAdminLogin,
        oauth_zoom_dashboard_login: oauthZoomDashboardLogin,
        oauth_webex_client_id: oauthWebexClientId.trim(),
        oauth_webex_client_secret: oauthWebexClientSecret.trim(),
        oauth_webex_scope: oauthWebexScope.trim(),
        oauth_zoom_client_id: oauthZoomClientId.trim(),
        oauth_zoom_client_secret: oauthZoomClientSecret.trim(),
        google_maps_key: googleMapsKey.trim(),
        paubox_api_key: pauboxApiKey.trim(),
        paubox_base_url: pauboxBaseUrl.trim(),
        paubox_email_address: pauboxEmailAddress.trim(),
        hl7_connector_jwt_secret: hl7ConnectorJwtSecret.trim(),
      };

      const response = await apiPatch(`/api/organizations/${organization.id}/settings`, settingsData);
      const data = await response.json();
      if (data.authData) {
        setStorage({
          user: data.authData.user,
          organizations: data.authData.organizations,
          orgMemberships: data.authData.orgMemberships,
          isDemoSession: data.authData.isDemoSession,
        });
      }
      showToast("Settings updated successfully!", "success");
    } catch (error) {
      console.error("Error updating settings:", error);
      showToast("Error updating settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    setIsTestingConnection(true);
    try {
      const response = await apiGet(`/api/organizations/${organization.id}/test-connection`);
      const data = await response.json();
      if (data.success) {
        showToast("Connection test successful!", "success");
      } else {
        showToast("Connection test failed.", "error");
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      showToast("Error testing connection.", "error");
    } finally {
      setIsTestingConnection(false);
      updateConnectionStatus(organization.id);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchAllowedDomains();
  }, [fetchSettings, fetchAllowedDomains]);

  useEffect(() => {
    if (jwksCopied) {
      const t = setTimeout(() => setJwksCopied(false), 2000);
      return () => clearTimeout(t);
    }
  }, [jwksCopied]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header isAdminPage={true} />
      <Toast />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader
          title="General Settings"
          description="Configure the general settings for your organization"
          organization={organization}
        />

        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
          <section className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Organization</h2>
            <div className="flex items-center gap-4 rounded-lg bg-gradient-to-r from-brand-50 to-brand-50/50 border border-brand-100 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-brand-700/80">Organization name</p>
                <p className="mt-0.5 truncate text-lg font-semibold text-gray-900">
                  {organizationName || "—"}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Directory jobs</h3>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={directoryJobsEnabled}
                  onChange={(e) => setDirectoryJobsEnabled(e.target.checked)}
                  disabled={disableInputs}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Enable nightly directory sync for this organization
                </span>
              </label>
              <p className="mt-1 text-sm text-gray-500">
                When enabled, practitioner and location data is refreshed daily from your EHR.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Allowed domains</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddDomain();
                      }
                    }}
                    placeholder="e.g. example.com"
                    disabled={disableInputs}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={handleAddDomain}
                    disabled={disableInputs || !isDomainValid(newDomain)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add
                  </button>
                </div>
                <StaticDataTable
                  tableId="allowed-domains"
                  columns={ALLOWED_DOMAIN_COLUMNS}
                  rows={domainRows}
                  updateUrlParams={false}
                  headerClass="bg-gray-50"
                  headerColor={false}
                  trailingColumnKey="Actions"
                  rowId={(item: Record<string, any>) => String(item['Domain'])}
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">EHR Configuration</h2>
            <SelectInput
              label="EHR Provider"
              placeholder="Select EHR Provider"
              value={ehrProvider}
              onChange={(e) => setEhrProvider(e.target.value as EhrProvider)}
              options={ehrProviderOptions}
              required={true}
              disabled={disableInputs}
            />
            <nav className="border-b border-gray-200 pt-4 mt-4" aria-label="EHR tabs">
              <div className="flex gap-1 overflow-x-auto overflow-y-hidden">
                {EHR_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setEhrTab(tab.key)}
                    className={twMerge(
                      "whitespace-nowrap px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors",
                      ehrTab === tab.key
                        ? "border-blue-600 text-blue-600 bg-white"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
            <div className="space-y-6 pt-6 border-t border-gray-200 mt-0">
              {ehrTab === "epic" && (
                <EpicConfigTab
                  clientId={clientId}
                  onClientIdChange={setClientId}
                  epicFrontendClientId={epicFrontendClientId}
                  onEpicFrontendClientIdChange={setEpicFrontendClientId}
                  epicClientSecret={epicClientSecret}
                  onEpicClientSecretChange={setEpicClientSecret}
                  baseOauthUrl={baseOauthUrl}
                  onBaseOauthUrlChange={setBaseOauthUrl}
                  jwksUrl={jwksUrl}
                  jwksCopied={jwksCopied}
                  onJwksCopy={() => {
                    navigator.clipboard.writeText(jwksUrl);
                    setJwksCopied(true);
                  }}
                  disableInputs={disableInputs}
                  connectionStatus={connectionStatus}
                  isTestingConnection={isTestingConnection}
                  isLoadingConnectionStatus={isLoadingConnectionStatus}
                  onTestConnection={testConnection}
                />
              )}
              {ehrTab === "cerner" && (
                <CernerConfigTab
                  baseOauthUrl={baseOauthUrl}
                  onBaseOauthUrlChange={setBaseOauthUrl}
                  cernerClientId={cernerClientId}
                  onCernerClientIdChange={setCernerClientId}
                  cernerClientSecret={cernerClientSecret}
                  onCernerClientSecretChange={setCernerClientSecret}
                  cernerSystemId={cernerSystemId}
                  onCernerSystemIdChange={setCernerSystemId}
                  cernerOrganizationId={cernerOrganizationId}
                  onCernerOrganizationIdChange={setCernerOrganizationId}
                  connectionStatus={connectionStatus}
                  isTestingConnection={isTestingConnection}
                  isLoadingConnectionStatus={isLoadingConnectionStatus}
                  disableInputs={disableInputs}
                  onTestConnection={testConnection}
                />
              )}
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">OAuth Configuration</h2>
            <nav className="border-b border-gray-200" aria-label="OAuth tabs">
              <div className="flex gap-1 overflow-x-auto overflow-y-hidden">
                {OAUTH_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setOauthTab(tab.key)}
                    className={twMerge(
                      "whitespace-nowrap px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors",
                      oauthTab === tab.key
                        ? "border-blue-600 text-blue-600 bg-white"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
            <div className="space-y-6 pt-6 border-t border-gray-200">
              {oauthTab === "epic" && (
                <OAuthEpicTab
                  adminLogin={oauthEpicAdminLogin}
                  onAdminLoginChange={setOauthEpicAdminLogin}
                  dashboardLogin={oauthEpicDashboardLogin}
                  onDashboardLoginChange={setOauthEpicDashboardLogin}
                  disableInputs={disableInputs}
                />
              )}
              {oauthTab === "google" && (
                <OAuthGoogleTab
                  adminLogin={oauthGoogleAdminLogin}
                  onAdminLoginChange={setOauthGoogleAdminLogin}
                  dashboardLogin={oauthGoogleDashboardLogin}
                  onDashboardLoginChange={setOauthGoogleDashboardLogin}
                  clientId={oauthGoogleClientId}
                  onClientIdChange={setOauthGoogleClientId}
                  clientSecret={oauthGoogleClientSecret}
                  onClientSecretChange={setOauthGoogleClientSecret}
                  disableInputs={disableInputs}
                />
              )}
              {oauthTab === "microsoft" && (
                <OAuthMicrosoftTab
                  adminLogin={oauthMicrosoftAdminLogin}
                  onAdminLoginChange={setOauthMicrosoftAdminLogin}
                  dashboardLogin={oauthMicrosoftDashboardLogin}
                  onDashboardLoginChange={setOauthMicrosoftDashboardLogin}
                  clientId={oauthMicrosoftClientId}
                  onClientIdChange={setOauthMicrosoftClientId}
                  clientSecret={oauthMicrosoftClientSecret}
                  onClientSecretChange={setOauthMicrosoftClientSecret}
                  disableInputs={disableInputs}
                />
              )}
              {oauthTab === "webex" && (
                <OAuthWebexTab
                  adminLogin={oauthWebexAdminLogin}
                  onAdminLoginChange={setOauthWebexAdminLogin}
                  dashboardLogin={oauthWebexDashboardLogin}
                  onDashboardLoginChange={setOauthWebexDashboardLogin}
                  clientId={oauthWebexClientId}
                  onClientIdChange={setOauthWebexClientId}
                  clientSecret={oauthWebexClientSecret}
                  onClientSecretChange={setOauthWebexClientSecret}
                  scope={oauthWebexScope}
                  onScopeChange={setOauthWebexScope}
                  disableInputs={disableInputs}
                />
              )}
              {oauthTab === "zoom" && (
                <OAuthZoomTab
                  adminLogin={oauthZoomAdminLogin}
                  onAdminLoginChange={setOauthZoomAdminLogin}
                  dashboardLogin={oauthZoomDashboardLogin}
                  onDashboardLoginChange={setOauthZoomDashboardLogin}
                  clientId={oauthZoomClientId}
                  onClientIdChange={setOauthZoomClientId}
                  clientSecret={oauthZoomClientSecret}
                  onClientSecretChange={setOauthZoomClientSecret}
                  disableInputs={disableInputs}
                />
              )}
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <GoogleMapsConfigTab
              apiKey={googleMapsKey}
              onApiKeyChange={setGoogleMapsKey}
              disableInputs={disableInputs}
            />
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <PauboxConfigTab
              apiKey={pauboxApiKey}
              onApiKeyChange={setPauboxApiKey}
              baseUrl={pauboxBaseUrl}
              onBaseUrlChange={setPauboxBaseUrl}
              emailAddress={pauboxEmailAddress}
              onEmailAddressChange={setPauboxEmailAddress}
              disableInputs={disableInputs}
            />
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <Hl7ConfigTab
              hl7ConnectorJwtSecret={hl7ConnectorJwtSecret}
              onHl7ConnectorJwtSecretChange={setHl7ConnectorJwtSecret}
              disableInputs={disableInputs}
            />
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <DocumentTypesSection />
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <ClinicalNoteTypesSection />
          </section>

          <div className="flex gap-4 items-center">
            <SubmitButton
              loading={isLoading}
              text="Save Settings"
              loadingText="Saving..."
              onClick={handleSubmit}
              className="max-w-xs"
              disabled={disableInputs}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneralSettings;
